"use client";

import { useEffect, useMemo, useState } from "react";

import { fitPointsToView } from "@/utils/mapboxStatic/mercator";
import { staticMapBaseUrl } from "@/utils/mapboxStatic/staticImageUrl";
import type { LngLat, MapViewport } from "@/utils/mapboxStatic/types";
import { useGeocode } from "@/utils/mapboxStatic/useGeocode";

import { haversineMiles } from "./destination";
import type { ScanStop } from "./scanJourney";

/** Fixed render size — drives both the geocode/fit math and the SVG viewBox. */
export const JOURNEY_MAP_WIDTH = 560;
export const JOURNEY_MAP_HEIGHT = 240;

export interface JourneyStop extends ScanStop {
  point: LngLat;
}

export type ScanJourneyStatus = "idle" | "loading" | "ready" | "empty";

export interface ScanJourney {
  /** `idle` (no stops) · `loading` (geocoding/image) · `ready` · `empty` (nothing geocoded). */
  status: ScanJourneyStatus;
  /** Resolved, geocoded stops in journey order. */
  stops: JourneyStop[];
  viewport: MapViewport | null;
  mapUrl: string | null;
  width: number;
  height: number;
  lastScanPoint: LngLat | null;
  destinationPoint: LngLat | null;
  distanceMiles: number | null;
}

/**
 * Preload an image (the static basemap) so the parent can gate a smooth
 * reveal on `loaded`. Errors are treated as "loaded" so the SVG overlay
 * still renders on top of a broken image rather than hanging in a spinner.
 */
function useImagePreload(url: string | null): { loaded: boolean } {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!url) {
      setLoaded(false);
      return;
    }
    setLoaded(false);
    const img = new Image();
    let cancelled = false;
    const finish = () => {
      if (!cancelled) setLoaded(true);
    };
    img.onload = finish;
    img.onerror = finish;
    img.src = url;
    return () => {
      cancelled = true;
    };
  }, [url]);
  return { loaded };
}

/**
 * Resolves a set of route stops into a render-ready map: geocodes them,
 * fits a viewport, builds the static basemap URL, and preloads that image.
 * `status` is `"ready"` only once coordinates AND the basemap image are in
 * hand, so a host can gate a single smooth reveal on it. Pass `[]` for
 * the idle state.
 *
 * Memoize the `stops` argument so geocoding stays cached.
 */
export function useScanJourneyMap(stops: ScanStop[]): ScanJourney {
  const queries = useMemo(() => stops.map((s) => s.query), [stops]);
  const geo = useGeocode(queries);

  const resolved = useMemo<JourneyStop[]>(() => {
    const out: JourneyStop[] = [];
    for (const stop of stops) {
      const point = geo.byQuery.get(stop.query);
      if (point) out.push({ ...stop, point });
    }
    return out;
  }, [stops, geo.byQuery]);

  const viewport = useMemo(
    () =>
      resolved.length > 0
        ? fitPointsToView(
            resolved.map((s) => s.point),
            JOURNEY_MAP_WIDTH,
            JOURNEY_MAP_HEIGHT,
          )
        : null,
    [resolved],
  );

  const mapUrl = useMemo(
    () =>
      viewport
        ? staticMapBaseUrl({
            center: viewport.center,
            zoom: viewport.zoom,
            width: JOURNEY_MAP_WIDTH,
            height: JOURNEY_MAP_HEIGHT,
          })
        : null,
    [viewport],
  );

  const { loaded: imageReady } = useImagePreload(mapUrl);

  const lastScanPoint = resolved.find((s) => s.kind === "last")?.point ?? null;
  const destinationPoint =
    resolved.find((s) => s.kind === "destination")?.point ?? null;
  const distanceMiles =
    lastScanPoint && destinationPoint
      ? haversineMiles(lastScanPoint, destinationPoint)
      : null;

  let status: ScanJourneyStatus;
  if (stops.length === 0) {
    status = "idle";
  } else if (geo.isLoading) {
    status = "loading";
  } else if (resolved.length === 0) {
    status = "empty";
  } else if (mapUrl && !imageReady) {
    status = "loading";
  } else {
    status = "ready";
  }

  return {
    status,
    stops: resolved,
    viewport,
    mapUrl,
    width: JOURNEY_MAP_WIDTH,
    height: JOURNEY_MAP_HEIGHT,
    lastScanPoint,
    destinationPoint,
    distanceMiles,
  };
}
